/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React from 'react';
import { get } from 'lodash';

import ActionListGrid from '@broadleaf/admin-components/dist/collection/components/ActionListGrid';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import ActionRegistrar from '@broadleaf/admin-components/dist/metadata/services/ActionRegistrar';
import useInvoicesGrid from '../../hooks/useInvoicesGrid';
import { getFetchInvoicesEndpoint } from '../../utils/InvoicesOperationUtils';

import messages from '../../utils/InvoicesGridUtils.messages';
import type { ICommonGridHeader } from '@broadleaf/admin-components/dist/types/common';
import { getDefaultInvoiceHeaders } from '../../utils/InvoicesGridUtils';
import { InvoiceGridDefinition } from '@src/oms/InvoicesView/utils/types';
import AdvancedSearchQueryBuilder from '@broadleaf/admin-components/dist/collection/components/AdvancedSearchQueryBuilder';
import GetInvoiceDetailsAction from '@src/oms/InvoicesView/components/action-components/GetInvoiceDetailsAction';
import ActionListGridQueryFilter from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/action-components/ActionListGridQueryFilter';
import GetInvoicePdfAction from '@src/oms/InvoicesView/components/action-components/GetInvoicePdfAction/GetInvoicePdfAction';
import { useLabelWithTotals } from '@src/oms/InvoicesView/hooks/useLabelWithTotals';

export const OtherInvoicesGrid: InvoiceGridDefinition = () => {
  const { collectionState, getActionListGridProps } = useInvoicesGrid({
    readConfig: getFetchInvoicesEndpoint(),
    invoiceStatuses: [
      'ORDER_PROCESSING',
      'SUBMITTED',
      'REIMBURSED',
      'REJECTED',
      'INVOICE_PAID',
      'DISPUTED',
      'READY_TO_SUBMIT',
      'OPEN_REVMED_INVOICE',
      'HOLD_FOR_REVIEW',
      'REQUIRES_PO',
      'CREDIT_PENDING'
    ],
    excludeInvoiceStatuses: true
  });

  const actionListGridProps = getActionListGridProps();
  const actionComponents = ActionRegistrar.getActionComponents(
    'View',
    OtherInvoicesGrid.VIEW_TYPE
  );
  const actionDefinitions = useReadyToSubmitActionDefinitions();
  const headers = useInvoiceHistoryHeaders();
  const label = useLabelWithTotals(collectionState, messages.otherInvoices);
  return (
    <ActionListGrid
      augmentationKey={''}
      {...actionListGridProps}
      actionComponents={actionComponents}
      actionDefinitions={actionDefinitions}
      headers={headers}
      label={label}
      sortable={true}
    />
  );
};

OtherInvoicesGrid.VIEW_TYPE = 'OTHER_INVOICES_GRID';
OtherInvoicesGrid.actionComponents = [
  {
    component: AdvancedSearchQueryBuilder,
    placement: 'ADVANCED_FILTER',
    type: 'QUERY_BUILDER'
  },
  {
    component: GetInvoiceDetailsAction,
    placement: 'ROW',
    type: 'VIEW_DETAILS'
  },
  {
    component: GetInvoicePdfAction,
    placement: 'ROW',
    type: 'DOWNLOAD_ORDER_INVOICE'
  },
  {
    component: ActionListGridQueryFilter,
    placement: 'QUERY_FILTER',
    type: 'QUERY'
  }
];

const useInvoiceHistoryHeaders = (): Array<ICommonGridHeader> => {
  const formatMessage = useFormatMessage();
  return ([
    ...getDefaultInvoiceHeaders(formatMessage),
    {
      classifier: 'Field',
      label: formatMessage(messages.headers.invoiceDate),
      name: 'invoiceDate',
      type: 'STRING',
      sortable: true,
      width: '110px',
      order: 350
    }
  ] as ICommonGridHeader[]).sort(
    (a, b) => get(a, 'order', 0) - get(b, 'order', 0)
  );
};

const useReadyToSubmitActionDefinitions = () => {
  const formatMessage = useFormatMessage();
  return [
    {
      label: formatMessage(messages.gridActions.viewDetails),
      placement: 'ROW',
      type: 'VIEW_DETAILS'
    },
    {
      label: formatMessage(messages.gridActions.downloadOrderInvoice),
      placement: 'ROW',
      type: 'DOWNLOAD_ORDER_INVOICE'
    },
    {
      attributes: {
        filterKey: 'query'
      },
      label: formatMessage(messages.gridActions.criteriaQuery),
      placement: 'QUERY_FILTER',
      type: 'QUERY'
    },
    {
      attributes: {
        filterKey: 'query_builder'
      },
      label: formatMessage(messages.gridActions.criteriaQuery),
      placement: 'ADVANCED_FILTER',
      type: 'QUERY_BUILDER'
    }
  ];
};

export default OtherInvoicesGrid;
